import { createContext, useCallback, useContext, useMemo, type JSX, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminTeams } from '@/hooks/useAdmin';
import { mainScopeLabel, DEFAULT_EXCLUDED_TEAMS, teamScopeParams, type TeamScopeParams } from '@/lib/teams';

/**
 * Which team an admin page is looking at.
 *
 * Normally the header's team switcher sets `?team=CODE` and every page reads
 * it here. With no team picked, a page shows the default teams (Social Media
 * + Vyral): DEFAULT_EXCLUDED_TEAMS in lib/teams (Influencer, Discord) are
 * excluded, and `excludeTeams` says which. Pages under a team section are
 * wrapped in <TeamScopeLock team="…">, which pins the scope and hides the
 * switcher — same page, that team's preset.
 */
const LockContext = createContext<string | null>(null);

export function TeamScopeLock({ team, children }: { team: string; children: ReactNode }): JSX.Element {
  return <LockContext.Provider value={team}>{children}</LockContext.Provider>;
}

const NONE: readonly string[] = [];

export interface TeamScope {
  /** Team code, or '' for the default (main teams) scope. */
  team: string;
  setTeam: (code: string) => void;
  /** True on a team-section page (Influencer, Vyral): the team is fixed. */
  locked: boolean;
  /**
   * Teams to leave out: DEFAULT_EXCLUDED_TEAMS when no team is picked on an
   * unlocked page, else empty. Send as `excludeTeam=A,B`.
   */
  excludeTeams: readonly string[];
  /** `team` / `excludeTeam` API params for this scope. */
  params: TeamScopeParams;
}

export function useTeamScope(): TeamScope {
  const locked = useContext(LockContext);
  const [params, setParams] = useSearchParams();
  const setTeam = useCallback(
    (code: string) => {
      if (locked) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (code) next.set('team', code);
          else next.delete('team');
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [locked, setParams],
  );
  const team = locked ?? params.get('team') ?? '';
  const excludeTeams = !locked && !team ? DEFAULT_EXCLUDED_TEAMS : NONE;
  const apiParams = useMemo(() => teamScopeParams({ team, excludeTeams }), [team, excludeTeams]);
  return { team, setTeam, locked: locked !== null, excludeTeams, params: apiParams };
}

/** Label for the default scope, e.g. "Social Media + Vyral". */
export function useMainScopeLabel(): string {
  const teams = useAdminTeams();
  return useMemo(() => mainScopeLabel(teams.data), [teams.data]);
}
